import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';
import { useTheme } from './ThemeProvider';
import { formatCurrencyReadable, formatPrice, parseFloatFromString } from '../utils/formatters';

const LIGHT_THEME = {
  background: '#fdf6e3',
  text: '#334155',
  grid: 'rgba(148, 163, 184, 0.45)',
  crosshair: 'rgba(51, 65, 85, 0.35)',
  border: 'rgba(148, 163, 184, 0.4)',
  line: '#2563eb',
  area: {
    top: 'rgba(37, 99, 235, 0.25)',
    bottom: 'rgba(37, 99, 235, 0.04)',
  },
  buys: 'rgba(34,197,94,0.45)',
  sells: 'rgba(248,113,113,0.55)',
};

const DARK_THEME = {
  background: '#0f172a',
  text: '#e2e8f0',
  grid: 'rgba(148, 163, 184, 0.2)',
  crosshair: 'rgba(226, 232, 240, 0.35)',
  border: 'rgba(148, 163, 184, 0.25)',
  line: '#38bdf8',
  area: {
    top: 'rgba(56, 189, 248, 0.35)',
    bottom: 'rgba(56, 189, 248, 0.08)',
  },
  buys: 'rgba(74,222,128,0.6)',
  sells: 'rgba(248,113,113,0.65)',
};

const getTheme = (isDark) => (isDark ? DARK_THEME : LIGHT_THEME);

const normalizeTime = (timeValue) => {
  if (typeof timeValue === 'number') {
    return timeValue > 1_000_000_000_000 ? Math.floor(timeValue / 1000) : timeValue;
  }
  if (typeof timeValue === 'string') {
    if (timeValue.includes('-')) {
      return Math.floor(new Date(timeValue).getTime() / 1000);
    }
    const parsed = parseInt(timeValue, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const computeMarkerSize = (volume, maxVolume) => {
  if (!maxVolume || maxVolume <= 0) return 1;
  const normalized = volume / maxVolume;
  const minSize = 0.8;
  const maxSize = 3.2;
  return Math.max(minSize, normalized * maxSize);
};

const BtcHeatmapChart = ({
  ticker = 'BTC/USD',
  priceHistory = [],
  heatmapPoints = [],
  isLoading = false,
  error,
  currency = 'USD',
  hideFooter = false,
}) => {
  const { darkMode } = useTheme();
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const lineSeriesRef = useRef(null);
  const areaSeriesRef = useRef(null);
  const [containerReady, setContainerReady] = useState(false);

  // Callback ref to detect when container is mounted
  const setContainerRef = useCallback((node) => {
    chartContainerRef.current = node;
    setContainerReady(!!node);
  }, []);

  const theme = getTheme(darkMode);

  const chartData = useMemo(() => {
    if (!Array.isArray(priceHistory)) return [];
    return priceHistory
      .map((point) => {
        const time = normalizeTime(point?.time);
        const price = typeof point?.price === 'number' ? point.price : parseFloatFromString(point?.price);
        if (!time || !price) return null;
        return { time, value: price };
      })
      .filter(Boolean)
      .sort((a, b) => a.time - b.time);
  }, [priceHistory]);

  const markers = useMemo(() => {
    if (!Array.isArray(heatmapPoints) || heatmapPoints.length === 0 || chartData.length === 0) {
      return [];
    }

    const totals = heatmapPoints
      .map((point) => {
        const timestamp = normalizeTime(point?.date);
        const buy = parseFloatFromString(point?.buyVolume);
        const sell = parseFloatFromString(point?.sellVolume);
        if (!timestamp || (!buy && !sell)) return null;
        return { timestamp, buy, sell };
      })
      .filter(Boolean);

    if (totals.length === 0) return [];

    const maxVolume = totals.reduce((max, { buy, sell }) => Math.max(max, buy || 0, sell || 0), 0);

    const trimTrailingZeros = (str) => str.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    const compact = (value) => trimTrailingZeros(formatCurrencyReadable(value || 0, currency));

    return totals.flatMap(({ timestamp, buy, sell }) => {
      const dayMarkers = [];
      if (buy > 0) {
        dayMarkers.push({
          time: timestamp,
          position: 'belowBar',
          color: theme.buys,
          shape: 'circle',
          size: computeMarkerSize(buy, maxVolume),
          text: compact(buy),
        });
      }
      if (sell > 0) {
        dayMarkers.push({
          time: timestamp,
          position: 'aboveBar',
          color: theme.sells,
          shape: 'circle',
          size: computeMarkerSize(sell, maxVolume),
          text: compact(sell),
        });
      }
      return dayMarkers;
    });
  }, [heatmapPoints, chartData, theme.buys, theme.sells, currency]);

  const totals = useMemo(
    () => ({
      buys: heatmapPoints.reduce((sum, point) => sum + parseFloatFromString(point?.buyVolume), 0),
      sells: heatmapPoints.reduce((sum, point) => sum + parseFloatFromString(point?.sellVolume), 0),
    }),
    [heatmapPoints]
  );

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        lineSeriesRef.current = null;
        areaSeriesRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!containerReady || !chartContainerRef.current || chartData.length === 0) {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        lineSeriesRef.current = null;
        areaSeriesRef.current = null;
      }
      return;
    }

    // Wait for container to have actual dimensions
    const containerWidth = chartContainerRef.current.clientWidth;
    if (containerWidth === 0) {
      // Container not visible yet, retry after a short delay
      const retryTimeout = setTimeout(() => {
        setContainerReady(false);
        requestAnimationFrame(() => setContainerReady(true));
      }, 50);
      return () => clearTimeout(retryTimeout);
    }

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      lineSeriesRef.current = null;
      areaSeriesRef.current = null;
    }

    const chart = createChart(chartContainerRef.current, {
      width: containerWidth,
      height: 420,
      layout: {
        background: { type: 'solid', color: theme.background },
        textColor: theme.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 12,
      },
      grid: {
        vertLines: { color: theme.grid, style: 1 },
        horzLines: { color: theme.grid, style: 1 },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: theme.crosshair, labelBackgroundColor: theme.background },
        horzLine: { color: theme.crosshair, labelBackgroundColor: theme.background },
      },
      rightPriceScale: {
        borderColor: theme.border,
        textColor: theme.text,
        scaleMargins: { top: 0.2, bottom: 0.2 },
      },
      timeScale: {
        borderColor: theme.border,
        timeVisible: true,
        secondsVisible: false,
      },
      localization: {
        priceFormatter: (price) => formatPrice(price, currency),
      },
      watermark: {
        visible: true,
        fontSize: 22,
        horzAlign: 'left',
        vertAlign: 'bottom',
        color: 'rgba(148, 163, 184, 0.35)',
        text: ticker,
      },
    });

    const lineSeries = chart.addLineSeries({
      color: theme.line,
      lineWidth: 2,
    });

    const areaSeries = chart.addAreaSeries({
      topColor: theme.area.top,
      bottomColor: theme.area.bottom,
      lineColor: theme.line,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    areaSeries.setData(chartData);
    lineSeries.setData(chartData);
    if (markers.length > 0) {
      lineSeries.setMarkers(markers);
    }

    chart.timeScale().fitContent();

    chartRef.current = chart;
    lineSeriesRef.current = lineSeries;
    areaSeriesRef.current = areaSeries;

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        lineSeriesRef.current = null;
        areaSeriesRef.current = null;
      }
    };
  }, [containerReady, chartData, markers, theme, ticker, currency]);

  if (error) {
    return (
      <div className="rounded-md bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 p-4 text-red-700 dark:text-red-200">
        <p className="font-semibold">Unable to load BTC heatmap</p>
        <p className="text-sm">{error.message || 'Unknown error'}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        <p className="text-sm">No BTC price history available yet.</p>
      </div>
    );
  }

  return (
    <div>
      <div
        ref={setContainerRef}
        className="w-full"
        style={{ height: '420px' }}
      />
      {!hideFooter && (
        <div className="flex flex-wrap gap-4 text-sm px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: theme.buys }} />
            <span className="text-gray-600 dark:text-gray-300">Buys</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatCurrencyReadable(totals.buys, currency)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: theme.sells }} />
            <span className="text-gray-600 dark:text-gray-300">Sells</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatCurrencyReadable(totals.sells, currency)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BtcHeatmapChart;

